import { useState } from 'react'

import { useQuery } from '@tanstack/react-query'
import { DefaultMenuItem } from 'ag-grid-enterprise'
import { Mail, Send } from 'lucide-react'
import { DateRange } from 'react-day-picker'
import { renderToString } from 'react-dom/server'

import { getT, getTMinus, toISO8601 } from '@fortem-monorepo/js-utilities'
import { numberFormatter } from '@fortem-monorepo/js-utilities'
import { CustomMenuItem } from '@fortem-monorepo/react-ag'
import { createColDefs, createContextMenu } from '@fortem-monorepo/react-ag'
import { CustomAgGrid } from '@fortem-monorepo/react-ag'
import { HeaderConfigBar, useApi } from '@fortem-monorepo/react-spa'
import { Widget, WidgetContent, WidgetHeaderWithQuery } from '@fortem-monorepo/react-spa'
import { useConfirmModal } from '@fortem-monorepo/react-spa'
import { useUserContext } from '@fortem-monorepo/react-spa'
import { DateRangePicker } from '@fortem-monorepo/react-ui'
import { Smartrade } from '@fortem-monorepo/web-sdk'

import { SmartradeTradesStatusCellRenderer2 } from '@/ag-grid/cell-renderers/SmartradeTradesStatusCellRenderer2'
import { Page } from '@/components/Page'
import { SMARTRADE } from '@/constants/links'
import {
  useResendConfirmationEmailMutation,
  useResendNotificationEmailMutation,
  useResendOrderMutation,
} from '@/react-query/mutations/smartrade'

const colDefs = createColDefs<Smartrade.GetSmartradeTradesData2[number]>([
  {
    field: 'trade_date',
    hide: true,
    width: 10,
    maxWidth: 10,
    rowGroup: true,
  },
  {
    field: 'trade_date_tsp',
    headerName: 'Trade Tsp',
    width: 150,
  },
  {
    field: 'settledate',
    headerName: 'Sett Date',
    width: 100,
  },
  {
    field: 'isin',
    width: 140,
  },
  {
    field: 'clordid',
    width: 100,
  },
  {
    field: 'side',
    width: 75,
  },
  {
    field: 'currency',
    headerName: 'Ccy',
    width: 75,
  },
  {
    field: 'orderqty',
    headerName: 'Qty',
    width: 80,
    type: 'rightAligned',
    valueFormatter: numberFormatter({
      delimiter: true,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }),
  },
  {
    field: 'ordtype',
    headerName: 'Type',
    width: 80,
  },
  {
    field: 'ordstatus',
    headerName: 'Status',
    width: 82,
    cellRenderer: SmartradeTradesStatusCellRenderer2,
  },
  {
    field: 'price',
    width: 85,
    type: 'rightAligned',
    valueFormatter: numberFormatter({
      delimiter: true,
      minimumFractionDigits: 3,
      maximumFractionDigits: 3,
    }),
    columnGroupShow: 'open',
  },
  {
    field: 'notional',
    width: 85,
    type: 'rightAligned',
    valueFormatter: numberFormatter({
      delimiter: true,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }),
    columnGroupShow: 'open',
  },
  {
    field: 'tofix',
    headerName: 'To Fix',
    width: 90,
    columnGroupShow: 'open',
  },
  {
    field: 'counterparty',
    width: 120,
  },
  {
    field: 'firm',
    width: 250,
  },
  {
    field: 'rejection_message',
    width: 500,
    columnGroupShow: 'open',
  },
])

export interface SmartradeTradesPageProps {}

export const SmartradeTradesPage: React.FC<SmartradeTradesPageProps> = ({}) => {
  const { hasPermissions } = useUserContext()

  const hasWriteAccess = hasPermissions((permissions) => ({
    permissions: [permissions.WRITE_SMARTRADE_TRADES],
  }))

  const [dates, setDates] = useState<DateRange>({
    from: getTMinus(14),
    to: getT(),
  })

  const params: Smartrade.GetSmartradeTradesParams2 = {
    st_dt: toISO8601(dates.from!),
    ed_dt: toISO8601(dates.to ?? dates.from!),
  }

  const api = useApi()

  const query = useQuery({
    queryKey: ['smartrade', 'trades', params],
    queryFn: () => api.smartrade.getTrades(params),
  })

  const resendConfirmationEmailMutation = useResendConfirmationEmailMutation()
  const resendNotificationEmailMutation = useResendNotificationEmailMutation()
  const resendOrderMutation = useResendOrderMutation()

  const {
    Dialog: ConfirmResendConfirmationEmailDialog,
    withConfirmation: confirmResendConfirmationEmail,
  } = useConfirmModal({
    title: 'Resend Confirmation Email',
    description:
      "Make sure you've selected the correct row, this will resend the confirmation email. Are you sure you want to do this?",
  })

  const {
    Dialog: ConfirmResendNotificationEmailDialog,
    withConfirmation: confirmResentNotificationEmail,
  } = useConfirmModal({
    title: 'Resend Notification Email',
    description:
      "Make sure you've selected the correct row, this will resend the notification email. Are you sure you want to do this?",
  })

  const { Dialog: ConfirmResendOrderDialog, withConfirmation: confirmResendOrder } =
    useConfirmModal({
      title: 'Resend Order',
      description:
        "Make sure you've selected the correct row, this will resend the entire order. Are you sure you want to do this?",
    })

  const getContextMenuItems = createContextMenu<Smartrade.GetSmartradeTradesData2[number]>(
    (params) => {
      const clordid = params.node?.data?.clordid!
      const isin = params.node?.data?.isin!
      const status = params.node?.data?.ordstatus ?? ''

      const isPending = ['0', '8'].includes(status)
      const isConfirmed = ['2'].includes(status)
      const isWIP = ['WIP'].includes(status)

      const resendConfirmationEmail: CustomMenuItem = {
        name: `Resend Confirmation Email | ${clordid} - ${isin}`,
        icon: renderToString(<Mail size={16} />),
        action: () =>
          confirmResendConfirmationEmail(() =>
            resendConfirmationEmailMutation.mutateAsync({ clordid, isin }),
          ),
      }

      const resentOrder: CustomMenuItem = {
        name: `Resend Order | ${isin}`,
        icon: renderToString(<Send size={16} />),
        action: () =>
          confirmResentNotificationEmail(() => resendOrderMutation.mutateAsync({ clordid, isin })),
      }

      const resendNotificationEmail: CustomMenuItem = {
        name: `Resend Notification Email | ${clordid}`,
        icon: renderToString(<Send size={16} />),
        action: () =>
          confirmResendOrder(() => resendNotificationEmailMutation.mutateAsync({ clordid, isin })),
      }

      return [
        ...(!isWIP && hasWriteAccess ? [resendNotificationEmail] : []),
        ...(isPending && hasWriteAccess ? [resentOrder] : []),
        ...(isConfirmed && hasWriteAccess ? [resendConfirmationEmail] : []),
        ...(!isWIP && hasWriteAccess ? ['separator' as DefaultMenuItem] : []),
        'copy',
        'copyWithGroupHeaders',
        'separator',
        'export',
      ]
    },
  )

  return (
    <Page metadata={{ title: 'Smartrade Trades' }} permissions={SMARTRADE.TRADES.permissions}>
      <ConfirmResendConfirmationEmailDialog />
      <ConfirmResendNotificationEmailDialog />
      <ConfirmResendOrderDialog />
      <HeaderConfigBar>
        <div className="flex items-center gap-2">
          <DateRangePicker date={dates} setDate={setDates} />
        </div>
      </HeaderConfigBar>
      <Widget exposed className={'h-full'}>
        <WidgetHeaderWithQuery title="Smartrade Trades" query={query} />
        <WidgetContent loading={query.isLoading}>
          <CustomAgGrid
            domLayout={'normal'}
            getContextMenuItems={getContextMenuItems}
            autoSizeStrategy={{ type: 'fitCellContents' }}
            rowData={query.data ?? []}
            columnDefs={colDefs}
          />
        </WidgetContent>
      </Widget>
    </Page>
  )
}
